import "./style.scss";

import React, { useState, forwardRef, useImperativeHandle } from "react";

import { IconButton, Switch, Table, TableBody, TableCell, TableRow } from "@mui/material";

import { AreaLine } from "./area-line";
import { AreaListSubLine } from "./area-list-sub-line";
import { AreaTile } from "./area-tile";
import { Icon } from "../../../../common/component/icon/icon";
import { stringValue } from "../../../../common/field/renderer";
import { Area } from "./area";
import { AreaDetail } from "./area-detail";
import { context } from "../../../../context/context";
import { dialogModel } from "../../../../common/component/dialog/dialog-model";
import { loaderModel } from "../../../../common/component/loader/loader-model";
import { t } from "i18next";
import { useEffectAsync } from "../../../../common/hooks/use-effect-async";
import { operationsService } from "../../../services/operations";
import { parcelsService } from "../../../services/parcels";
import { orderBy } from "lodash";
import { DeliveryOrder } from "../../../model/delivery-order";
import { Parcel } from "../../../model/parcel";
import { stocksService } from "../../../services/stocks";
import { notificationModel } from "../../../../common/component/notifications/notification-model";
import { getLabelFormat } from "../../../../constants/constants";
import { qzManager } from "../../../qz/qz-manager";
import { menuOperationModel } from "../../preparation-menu/preparation-menu-model";
import { FeatureToggle, IsFeatureToggleEnabled } from "../../../../helpers/is-feature-toggle-enabled";

export interface IAreaListProps {
    operationCode: string;
    batchId: number;
}

export interface IAreaListRef {
    reloadList: () => void;
}

export const AreaList = forwardRef((props: IAreaListProps, ref) => {
    const [list, setList] = useState<Area[]>([]);
    const [listMode, setListMode] = useState("tile");
    const [reload, setReload] = useState(false);

    useEffectAsync(async () => {
        if (!isOperationAndBatchSelected()) {
            return;
        }

        const [deliveryOrderList, parcels] = await Promise.all([
            await operationsService.loadDeliveryOrderList(props.operationCode, props.batchId),
            await parcelsService.loadParcels(props.operationCode, props.batchId, "New"),
        ]);

        setList(orderBy(parcels.filter(p => p.area !== null).map(p =>
            Area.createForParcel(p, findOrderForParcelOrError(deliveryOrderList, p))), "areaNumber", "asc"));

        setReload(false);
    }, [props.operationCode, props.batchId, reload]);

    useImperativeHandle(ref, () => ({
        reloadList() {
            setReload(true);
        },
    }));

    const findOrderForParcelOrError = (deliveryOrderList: DeliveryOrder[], parcel: Parcel) => {
        const deliveryOrder = deliveryOrderList.find(d => d.deliveryOrderId === parcel.deliveryOrderId);

        if (deliveryOrder === undefined) {
            throw new Error("No order matching parcel was found in this batch.");
        }

        return deliveryOrder;
    };

    const isOperationAndBatchSelected = () => {
        return props.operationCode !== undefined && props.batchId !== undefined;
    };

    const renderLine = (areaItem: Area) => {
        return [<AreaLine areaItem={areaItem}
            key={`par-${areaItem.parcelId}`} />,
        renderDeliveryOrderDetailLineHeader(areaItem.parcelId),
        ...areaItem.details.map((detail, idx) => renderParcelDetailLine(areaItem, detail, idx)),
        ];
    };

    const renderTile = (areaItem: Area) => {
        return <AreaTile
            area={areaItem}
            key={`par-${areaItem.parcelId}`}
        />;
    };

    const renderSwitchMode = () => {
        return <div className="switchMode">
            <Icon icon="view_comfy"
                  tooltipTitle="components.area.action.viewSummarizeTooltip"
                  tooltipPlacement="top-start" />
            <Switch onChange={() => listMode === "tile" ? setListMode("list") : setListMode("tile")} data-testid="switchListMode"/>
            <Icon icon="list"
                  tooltipTitle="components.area.action.viewDetailsTooltip"
                  tooltipPlacement="top-start" />
        </div>;
    };

    const renderListContent = () => {
        if (listMode === "list") {
            return <Table>
                <TableBody className="table-body area-detailed-list">
                    {list.map((row, _idx) => renderLine(row))}
                </TableBody>
            </Table>;
        }

        return <div className="listSummarize" data-testid="listSummarize">
            {list.map((row, _idx) => renderTile(row))}
        </div>;
    };

    const renderDeliveryOrderDetailLineHeader = (parcelId: number) => {
        return <AreaListSubLine key={`par-header-${parcelId}`} />;
    };

    const renderParcelDetailLine = (area: Area, areaDetail: AreaDetail, key: number) => {
        return <TableRow key={"detail-" + key} data-testid={`detail-area-${area.areaNumber}-ean-${areaDetail.eansList}`} className="level2 content">
            <TableCell />
            <TableCell>{stringValue(areaDetail, "productId")}</TableCell>
            <TableCell>
                <ul>{areaDetail.eansList.map(ean => <li key={ean}>{ean}</li>)}</ul>
            </TableCell>
            <TableCell>{stringValue(areaDetail, "supplierReference")}</TableCell>
            <TableCell colSpan={2}>{stringValue(areaDetail, "labelReference")}</TableCell>
            <TableCell>{stringValue(areaDetail, "parcelledQuantity")}</TableCell>
            <TableCell className={IsFeatureToggleEnabled(FeatureToggle.STOCKOUTS) ? "area-detailed-list__stockout-button-container" : ""}>
                {stringValue(areaDetail, "availableQuantity")}
                {IsFeatureToggleEnabled(FeatureToggle.STOCKOUTS) && areaDetail.availableQuantity > 0 &&
                    <IconButton
                        data-testid={`stockout-button`}
                        className={"area-detailed-list__stockout-icon-button"}
                        onClick={() => confirmStockoutRemainingQuantityForAreaItem(area, areaDetail)}>
                        <Icon
                            color="primary"
                            className={"area-detailed-list-list__stockout-icon"}
                            icon={"report_problem"}
                            tooltipTitle="components.area.stockoutAction.title" />
                    </IconButton>}
            </TableCell>
            {IsFeatureToggleEnabled(FeatureToggle.STOCKOUTS) &&
                <TableCell>{stringValue(areaDetail, "stockoutQuantity")}</TableCell>}
            <TableCell>{stringValue(areaDetail, "productWeight")}</TableCell>
        </TableRow>;
    };

    const confirmStockoutRemainingQuantityForAreaItem = (areaItem: Area, areaItemDetail: AreaDetail) => {
        dialogModel.confirmBox(
            t("components.area.stockoutAction.confirmTitle"),
            t("components.area.stockoutAction.confirmDescription", {
                productName: areaItemDetail.labelReference,
            }),
            async () => await stockoutRemainingQuantityForAreaItem(areaItem, areaItemDetail),
        );
    };

    const stockoutRemainingQuantityForAreaItem = async (area: Area, areaDetail: AreaDetail) => {
        const actionId = loaderModel.foregroundActionStart(t("components.area.stockoutAction.inProgress"));

        await stockoutProductFromArea(area, areaDetail);
        setReload(true);

        loaderModel.foregroundActionEnd(actionId);
    };

    const stockoutProductFromArea = async (area: Area, areaDetail: AreaDetail) => {
        await stocksService.stockoutDeliveryOrderDetailByProductId(
            area.operationCode,
            area.batchId,
            area.deliveryOrderId,
            areaDetail.productId,
        );
        areaDetail.stockoutRemainingQuantity();

        if (area.isFulfilled) {
            await printParcelLabel(area.operationCode, area.batchId, area.parcelId);

            notificationModel.addSuccessMessage(t("components.pickAndPack.stockoutAction.completedAndLabelled"));
            await refreshOperationCounters(area.operationCode, area.batchId);

            return;
        }

        notificationModel.addSuccessMessage(t("components.pickAndPack.stockoutAction.completed"));
    };

    const printParcelLabel = async (operationCode: string, batchId: number, parcelId: number) => {
        const label = await parcelsService.label(operationCode, batchId, parcelId, getLabelFormat(context.settings.printerType));

        await qzManager.qzPrintOrDownloadFile(label.Value!, context.settings.printerType);
    };

    const refreshOperationCounters = async (operationCode: string, batchId: number) => {
        await menuOperationModel.refreshCounters(operationCode, batchId);
    };

    return <div>
        {renderSwitchMode()}
        {renderListContent()}
    </div>;
});
